import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PropertyCompletionService } from '../../services/property-completion.service';
import {
  PropertyCompletionReportResponse,
  PropertyCompletionActionResponse,
} from '../../models/property-completion';

@Component({
  selector: 'app-property-completion',
  standalone: true,
  imports: [
    CommonModule,
    FileUploadModule,
    ButtonModule,
    CardModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './property-completion.html',
  styleUrl: './property-completion.scss',
})
export class PropertyCompletion {
  private readonly propertyCompletionService = inject(PropertyCompletionService);
  private readonly messageService = inject(MessageService);

  currentStep: 'upload' | 'report' | 'completed' = 'upload';
  isLoading = false;
  uploadedFile: File | null = null;
  reportData: PropertyCompletionReportResponse | null = null;
  completionResult: PropertyCompletionActionResponse | null = null;

  onFileSelect(event: any, fileUpload: FileUpload) {
    const file = event.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid File',
        detail: 'Please upload a CSV file',
      });
      fileUpload.clear();
      return;
    }

    this.uploadedFile = file;
  }

  async onNext() {
    if (!this.uploadedFile) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No File',
        detail: 'Please select a CSV file first',
      });
      return;
    }

    this.isLoading = true;
    try {
      this.reportData = await this.propertyCompletionService.uploadAndGenerateReport(
        this.uploadedFile
      );

      if (this.reportData.duplicateLoopIds.length > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Duplicates Found',
          detail: `${this.reportData.duplicateLoopIds.length} duplicate Loop IDs were removed`,
        });
      }

      if (this.reportData.errors.length > 0) {  // Changed from errorLoopIds
        this.messageService.add({
          severity: 'warn',
          summary: 'Errors Found',
          detail: `${this.reportData.errors.length} properties have errors`,  // Updated message
        });
      }

      this.currentStep = 'report';
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.error?.error || 'Failed to process CSV file',
      });
    } finally {
      this.isLoading = false;
    }
  }

  async onAutoComplete() {
    if (!this.reportData?.actionableLoopIds || this.reportData.actionableLoopIds.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Action Needed',
        detail: 'All properties are already exchanged or completed',
      });
      return;
    }

    this.isLoading = true;
    try {
      this.completionResult = await this.propertyCompletionService.executeCompletion({
        loopIds: this.reportData.actionableLoopIds,
      });

      if (this.completionResult.failureCount > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Partial Success',
          detail: `${this.completionResult.successCount} properties completed, ${this.completionResult.failureCount} failed`,
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: 'Completed',
          detail: `Successfully completed ${this.completionResult.successCount} properties`,
        });
      }

      this.currentStep = 'completed';
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Completion Failed',
        detail: error.error?.error || 'Failed to complete properties',
      });
    } finally {
      this.isLoading = false;
    }
  }

  onCancel() {
    this.reset();
  }

  reset() {
    this.currentStep = 'upload';
    this.uploadedFile = null;
    this.reportData = null;
    this.completionResult = null;
    this.isLoading = false;
  }
}
